import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  useUser,
  useBlockUser,
  useUnblockUser,
  useInvalidateUserTokens,
  useTerminateSubscription,
  useExtendSubscription,
} from '../services/usersApi';
import { ConfirmationModal } from '../components/ConfirmationModal';
import type {
  SubscriptionPlan,
  SubscriptionStatus,
  SubscriptionStore,
  UserAction,
} from '../types/user.types';

// Subscription status badge styling
const getSubscriptionBadgeStyle = (status?: SubscriptionStatus): string => {
  switch (status) {
    case 'trial':
      return 'bg-yellow-100 text-yellow-800';
    case 'active':
      return 'bg-green-100 text-green-800';
    case 'canceled':
      return 'bg-orange-100 text-orange-800';
    case 'expired':
      return 'bg-red-100 text-red-800';
    case 'none':
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

// Subscription status label
const getSubscriptionLabel = (status?: SubscriptionStatus): string => {
  switch (status) {
    case 'trial':
      return 'Trial';
    case 'active':
      return 'Active';
    case 'canceled':
      return 'Canceled';
    case 'expired':
      return 'Expired';
    case 'none':
    default:
      return 'None';
  }
};

// Subscription plan display name
const getPlanDisplayName = (plan?: SubscriptionPlan | null): string => {
  switch (plan) {
    case 'premium_monthly':
      return 'Premium Monthly';
    case 'premium_yearly':
      return 'Premium Yearly';
    default:
      return 'None';
  }
};

// Store display name
const getStoreDisplayName = (store?: SubscriptionStore | null): string => {
  switch (store) {
    case 'google_play':
      return 'Google Play';
    case 'rustore':
      return 'RuStore';
    default:
      return 'N/A';
  }
};

interface ConfirmationState {
  isOpen: boolean;
  action: UserAction | null;
}

export function UserDetailView() {
  const { userId } = useParams<{ userId: string }>();
  const { data: user, isLoading, error } = useUser(userId!);
  const blockUserMutation = useBlockUser();
  const unblockUserMutation = useUnblockUser();
  const invalidateTokensMutation = useInvalidateUserTokens();
  const terminateSubscriptionMutation = useTerminateSubscription();
  const extendSubscriptionMutation = useExtendSubscription();

  const [confirmationState, setConfirmationState] = useState<ConfirmationState>({
    isOpen: false,
    action: null,
  });
  const [extendDays, setExtendDays] = useState<number>(30);

  // Format date helper
  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'Never';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(new Date(dateString));
  };

  // Handle user actions
  const handleAction = (action: UserAction) => {
    setConfirmationState({
      isOpen: true,
      action,
    });
  };

  const handleConfirm = async () => {
    if (!userId) return;

    try {
      switch (confirmationState.action) {
        case 'block':
          await blockUserMutation.mutateAsync(userId);
          toast.success('User blocked successfully');
          break;
        case 'unblock':
          await unblockUserMutation.mutateAsync(userId);
          toast.success('User unblocked successfully');
          break;
        case 'forceLogout':
          await invalidateTokensMutation.mutateAsync(userId);
          toast.success('User logged out successfully');
          break;
        case 'terminateSubscription':
          await terminateSubscriptionMutation.mutateAsync(userId);
          toast.success('Subscription terminated');
          break;
        case 'extendSubscription':
          await extendSubscriptionMutation.mutateAsync({ userId, days: extendDays });
          toast.success(`Subscription extended by ${extendDays} days`);
          break;
      }
      setConfirmationState({ isOpen: false, action: null });
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message || 'Operation failed';
      toast.error(message);
    }
  };

  const handleCancel = () => {
    setConfirmationState({ isOpen: false, action: null });
  };

  // Get modal content based on action
  const getModalContent = () => {
    const { action } = confirmationState;
    switch (action) {
      case 'block':
        return {
          title: 'Block User',
          message: `Are you sure you want to block ${user?.email}?`,
          confirmText: 'Block',
        };
      case 'unblock':
        return {
          title: 'Unblock User',
          message: `Are you sure you want to unblock ${user?.email}?`,
          confirmText: 'Unblock',
        };
      case 'forceLogout':
        return {
          title: 'Force Logout',
          message: `Force logout ${user?.email}? This will invalidate all their tokens.`,
          confirmText: 'Force Logout',
        };
      case 'terminateSubscription':
        return {
          title: 'Terminate Subscription',
          message: `Terminate subscription for ${user?.email}? This will immediately revoke access.`,
          confirmText: 'Terminate',
        };
      case 'extendSubscription':
        return {
          title: 'Extend Subscription',
          message: `Extend subscription for ${user?.email} by ${extendDays} day${extendDays !== 1 ? 's' : ''}?`,
          confirmText: 'Extend',
        };
      default:
        return {
          title: 'Confirm Action',
          message: 'Are you sure?',
          confirmText: 'Confirm',
        };
    }
  };

  const modalContent = getModalContent();
  const isActionLoading =
    blockUserMutation.isPending ||
    unblockUserMutation.isPending ||
    invalidateTokensMutation.isPending ||
    terminateSubscriptionMutation.isPending ||
    extendSubscriptionMutation.isPending;

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900">Loading User Details...</h1>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-gray-200 rounded w-1/3"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900">Error Loading User</h1>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <p className="text-red-800 mb-4">
            Error loading user details: {error.message || 'Unknown error'}
          </p>
          <Link
            to="/users"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Back to Users List
          </Link>
        </div>
      </div>
    );
  }

  // Not found state
  if (!user) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900">User Not Found</h1>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <p className="text-yellow-800 mb-4">
            The user you are looking for does not exist or has been deleted.
          </p>
          <Link
            to="/users"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Back to Users List
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">User Details</h1>
        <Link to="/users" className="text-blue-600 hover:text-blue-800 font-medium">
          ← Back to Users List
        </Link>
      </div>

      {/* User Profile Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Profile Information</h2>

        {/* User ID - Full Width */}
        <div className="mb-6 pb-6 border-b border-gray-200">
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">
            User ID
          </h3>
          <p className="text-base text-gray-900 font-mono break-all">{user.id}</p>
        </div>

        {/* Grid Layout for Other Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Email */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">
              Email
            </h3>
            <p className="text-base text-gray-900">{user.email}</p>
          </div>

          {/* Role */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">Role</h3>
            <p className="text-base text-gray-900 capitalize">{user.role}</p>
          </div>

          {/* Native Language */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">
              Native Language
            </h3>
            <p className="text-base text-gray-900">
              {user.nativeLanguage ? user.nativeLanguage.toUpperCase() : 'Not set'}
            </p>
          </div>

          {/* Email Verified */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">
              Email Verified
            </h3>
            <div className="flex flex-col gap-1">
              <span
                className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full w-fit ${
                  user.isVerified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                }`}
              >
                {user.isVerified ? 'Verified' : 'Not Verified'}
              </span>
              {user.emailVerifiedAt && (
                <span className="text-xs text-gray-600">{formatDate(user.emailVerifiedAt)}</span>
              )}
            </div>
          </div>

          {/* Onboarding Status */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">
              Onboarding
            </h3>
            <div className="flex flex-col gap-1">
              <span
                className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full w-fit ${
                  user.onboardingCompleted
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {user.onboardingCompleted ? 'Completed' : 'Not Completed'}
              </span>
              {user.onboardingCompletedAt && (
                <span className="text-xs text-gray-600">
                  {formatDate(user.onboardingCompletedAt)}
                </span>
              )}
            </div>
          </div>

          {/* Status */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">
              Status
            </h3>
            <span
              className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}
            >
              {user.isActive ? 'Active' : 'Blocked'}
            </span>
          </div>

          {/* Created Date */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">
              Created Date
            </h3>
            <p className="text-base text-gray-900">{formatDate(user.createdAt)}</p>
          </div>

          {/* Updated Date */}
          {user.updatedAt && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">
                Last Updated
              </h3>
              <p className="text-base text-gray-900">{formatDate(user.updatedAt)}</p>
            </div>
          )}
        </div>
      </div>

      {/* Subscription Information Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Subscription Information</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Subscription Status */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">
              Status
            </h3>
            <span
              className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getSubscriptionBadgeStyle(user.subscriptionStatus)}`}
            >
              {getSubscriptionLabel(user.subscriptionStatus)}
            </span>
          </div>

          {/* Subscription Plan */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">Plan</h3>
            <p className="text-base text-gray-900">{getPlanDisplayName(user.subscriptionPlan)}</p>
          </div>

          {/* Store */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">
              Store
            </h3>
            <p className="text-base text-gray-900">{getStoreDisplayName(user.subscriptionStore)}</p>
          </div>

          {/* Period End */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">
              Period End
            </h3>
            <p className="text-base text-gray-900">{formatDate(user.subscriptionPeriodEnd)}</p>
          </div>

          {/* Cancel at Period End */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">
              Cancel at Period End
            </h3>
            <span
              className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                user.subscriptionCancelAtPeriodEnd
                  ? 'bg-orange-100 text-orange-800'
                  : 'bg-gray-100 text-gray-800'
              }`}
            >
              {user.subscriptionCancelAtPeriodEnd ? 'Yes' : 'No'}
            </span>
          </div>

          {/* Trial Used */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">
              Trial Used
            </h3>
            <span
              className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                user.subscriptionTrialUsed
                  ? 'bg-gray-100 text-gray-800'
                  : 'bg-green-100 text-green-800'
              }`}
            >
              {user.subscriptionTrialUsed ? 'Yes' : 'No'}
            </span>
          </div>
        </div>

        {/* Subscription Actions */}
        {
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">
              Subscription Actions
            </h3>
            <div className="flex items-end gap-4">
              {(user.subscriptionStatus === 'active' ||
                user.subscriptionStatus === 'trial' ||
                user.subscriptionStatus === 'canceled') && (
                <button
                  type="button"
                  onClick={() => handleAction('terminateSubscription')}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  Terminate Subscription
                </button>
              )}
              <div className="flex items-end gap-2">
                <div>
                  <label
                    htmlFor="extendDays"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Days
                  </label>
                  <input
                    id="extendDays"
                    type="number"
                    min={1}
                    max={365}
                    value={extendDays}
                    onChange={(e) =>
                      setExtendDays(Math.max(1, Math.min(365, parseInt(e.target.value) || 1)))
                    }
                    className="w-20 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => handleAction('extendSubscription')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Extend Subscription
                </button>
              </div>
            </div>
          </div>
        }
      </div>

      {/* Action Buttons */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">User Actions</h2>
        <div className="flex gap-4">
          {user.isActive ? (
            <button
              type="button"
              onClick={() => handleAction('block' as UserAction)}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              Block User
            </button>
          ) : (
            <button
              type="button"
              onClick={() => handleAction('unblock' as UserAction)}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              Unblock User
            </button>
          )}
          <button
            type="button"
            onClick={() => handleAction('forceLogout' as UserAction)}
            className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            Force Logout
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationState.isOpen}
        title={modalContent.title}
        message={modalContent.message}
        confirmText={modalContent.confirmText}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        isLoading={isActionLoading}
      />
    </div>
  );
}
